import { Group, rem, Stack, Title } from "@mantine/core";
import { IconHelp, IconInbox, IconLifebuoy, IconServerCog, IconSettings } from "@tabler/icons-react";
import { useRouterState } from "@tanstack/react-router";
import { IconIpaws } from "shared/components/global/IconIpaws";
import { SidebarLink, SidebarSubLink } from "shared/components/global/Sidebar/SidebarLink";
import { features } from "shared/graphql/features";
import { useUser } from "shared/graphql/users";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { useIsSupport } from "shared/hooks/useIsSupport";
import { usePermissionCheck } from "shared/stores/oidc";
import { to } from "shared/utils/fns";
import { Permission } from "src/gql/graphql";
import { match } from "ts-pattern";

export function useSublinkCount() {
    return useMessagesSublinks().length + useOrganizationSublinks().length + useIpawsSublinks().length;
}

export function useActiveSublink() {
    const { matches } = useRouterState();

    const messageSublinks = useMessagesSublinks();
    const organizationSublinks = useOrganizationSublinks();
    const ipawsSublinks = useIpawsSublinks();
    const helpSublinks = useHelpSublinks();
    const settingsSublinks = useSettingsSublinks();

    return match(matches.map(to("fullPath")))
        .when(
            (paths) => paths.some((p) => p.includes("/help")),
            () => ({ header: "Help", data: helpSublinks }),
        )
        .when(
            (paths) => paths.some((p) => p.includes("/messages")),
            () => ({ header: "Messages", data: messageSublinks }),
        )
        .when(
            (paths) => paths.some((p) => p.includes("/organization")),
            () => ({ header: "Organization", data: organizationSublinks }),
        )
        .when(
            (paths) => paths.some((p) => p.includes("/ipaws")),
            () => ({ header: "IPAWS", data: ipawsSublinks }),
        )
        .when(
            (paths) => paths.some((p) => p.includes("/settings")),
            () => ({ header: "Settings", data: settingsSublinks }),
        )
        .otherwise(() => null);
}

function useMessagesSublinks() {
    const sendFromGroup = usePermissionCheck(Permission.GroupMessageSendFrom);
    const scheduleMessage = usePermissionCheck(Permission.GroupMessageSchedule);
    const createTemplate = usePermissionCheck(Permission.GroupTemplateCreate);
    const updateTemplate = usePermissionCheck(Permission.GroupTemplateUpdate);
    const deleteTemplate = usePermissionCheck(Permission.GroupTemplateDelete);

    return [
        <SidebarSubLink key="/messages/inbox" to="/messages/inbox" label="Inbox" />,
        sendFromGroup && <SidebarSubLink key="/messages/drafts" to="/messages/drafts" label="Drafts" />,
        scheduleMessage && <SidebarSubLink key="/messages/scheduled" to="/messages/scheduled" label="Scheduled" />,
        sendFromGroup && <SidebarSubLink key="/messages/sent" to="/messages/sent" label="Sent" />,
        (sendFromGroup || createTemplate || updateTemplate || deleteTemplate) && (
            <SidebarSubLink key="/messages/templates" to="/messages/templates" label="Templates" />
        ),
    ].filter((route) => !!route);
}

function useOrganizationSublinks() {
    features.useLoader();

    const hasTopics = features.enabled("HAS_TOPICS");
    const hasOptIn = features.enabled("HAS_WEB_OPT_IN");
    const manageGroup = usePermissionCheck(Permission.GroupSubgroupManage);
    const manageTopics = usePermissionCheck(Permission.TopicCategoryManage);
    const manageSenders = usePermissionCheck(Permission.GroupSenderManage);
    const manageGeolocation = usePermissionCheck(Permission.CustomerShapeManage);
    const groupRoles = usePermissionCheck(Permission.GroupRolesManage);
    const topicRoles = usePermissionCheck(Permission.TopicRolesManage);
    const customerRoles = usePermissionCheck(Permission.CustomerRolesManage);
    const customerUserUpdate = usePermissionCheck(Permission.CustomerUsersUpdate);

    return [
        manageGroup && <SidebarSubLink key="/organization/groups" to="/organization/groups" label="Groups" />,
        hasTopics && manageTopics && <SidebarSubLink key="/organization/topics" to="/organization/topics" label="Topics" />,
        manageSenders && <SidebarSubLink key="/organization/senders" to="/organization/senders" label="Senders" />,
        manageGeolocation && <SidebarSubLink key="/organization/geolocations" to="/organization/geolocations" label="Geo-Locations" />,
        (groupRoles || topicRoles || customerRoles) && <SidebarSubLink key="/organization/users" to="/organization/users" label="Users" />,
        hasOptIn && customerUserUpdate && <SidebarSubLink key="/organization/opt-in" to="/organization/opt-in" label="Opt-in Users" />,
    ].filter((route) => !!route);
}

function useIpawsSublinks() {
    features.useLoader();

    const hasIpaws = features.enabled("HAS_IPAWS");
    const sendAlert = usePermissionCheck(Permission.IpawsMessageSend);
    const modifyAlert = usePermissionCheck(Permission.IpawsAlertModify);
    const updateAlert = usePermissionCheck(Permission.IpawsMessageUpdate);
    const cancelAlert = usePermissionCheck(Permission.IpawsMessageCancel);
    const viewHistory = usePermissionCheck(Permission.IpawsHistoryView);
    const viewProfile = usePermissionCheck(Permission.IpawsProfileView);
    const customerManage = usePermissionCheck(Permission.CustomerIpawsManage);

    const alertPermissions = sendAlert || modifyAlert || updateAlert || cancelAlert || viewHistory;

    if (!hasIpaws) return [];
    return [
        alertPermissions && <SidebarSubLink key="/ipaws/alerts" to="/ipaws/alerts" label="Alerts" />,
        alertPermissions && <SidebarSubLink key="/ipaws/drafts" to="/ipaws/drafts" label="Drafts" />,
        alertPermissions && <SidebarSubLink key="/ipaws/templates" to="/ipaws/templates" label="Templates" />,
        (viewProfile || customerManage) && <SidebarSubLink key="/ipaws/certificates" to="/ipaws/certificates" label="Certificates" />,
    ].filter((route) => !!route);
}

function useHelpSublinks() {
    features.useLoader();

    const hasTopics = features.enabled("HAS_TOPICS");
    const manageTopics = usePermissionCheck(Permission.TopicCategoryManage);
    const ipawsSublinks = useIpawsSublinks();
    const organizationSublinks = useOrganizationSublinks();
    const messagesSublinks = useMessagesSublinks();

    return [
        messagesSublinks.length > 1 && <SidebarSubLink key="/help/messages" to="/help/messages" label="Composing a message" />,
        organizationSublinks.length > 0 && (
            <SidebarSubLink key="/help/organization" to="/help/organization" label="Organization management" />
        ),
        hasTopics && manageTopics && <SidebarSubLink key="/help/topics" to="/help/topics" label="Topics" />,
        ipawsSublinks.length > 0 && <SidebarSubLink key="/help/ipaws" to="/help/ipaws" label="IPAWS" />,
    ].filter((route) => !!route);
}

function useSettingsSublinks() {
    features.useLoader();

    const { user } = useUser();
    const hasTopics = features.enabled("HAS_TOPICS");

    return [
        <SidebarSubLink key="profile" to="/settings" hash="profile" label="Profile Info" />,
        <SidebarSubLink key="emails" to="/settings" hash="emails" label="Emails" />,
        <SidebarSubLink key="texts" to="/settings" hash="texts" label="Text Numbers" />,
        <SidebarSubLink key="voice" to="/settings" hash="voice" label="Call Numbers" />,
        <SidebarSubLink key="devices" to="/settings" hash="devices" label="Devices" />,
        <SidebarSubLink key="locations" to="/settings" hash="locations" label="Locations" />,
        hasTopics && <SidebarSubLink key="topics" to="/settings" hash="topics" label="Topics" />,
        (user?.groups.length ?? 0) > 0 && <SidebarSubLink key="groups" to="/settings" hash="groups" label="Groups" />,
        <SidebarSubLink key="delete" to="/settings" hash="delete" label="Delete Account" />,
    ].filter((route) => !!route);
}

const SecondaryNav = () => {
    const activeSublink = useActiveSublink();

    if (!activeSublink) return null;

    return (
        <Stack justify="flex-start" h="100%" w={rem(175)} style={{ borderLeft: "1px solid var(--app-shell-border-color)" }}>
            <Stack
                py="xs"
                h="7dvh"
                mih={68}
                justify="center"
                align="center"
                fw="bold"
                style={{
                    borderBottom: "1px solid var(--app-shell-border-color)",
                }}
            >
                <Title order={1} fz="medium" fw="bold">
                    {activeSublink.header}
                </Title>
            </Stack>
            <Stack gap="xs">{activeSublink.data}</Stack>
        </Stack>
    );
};

export const Sidebar = () => {
    const isMobile = useIsMobile();
    const activeSublink = useActiveSublink();

    return isMobile ? <Stack mt="xs">{activeSublink?.data}</Stack> : <SidebarDesktop />;
};

const SidebarDesktop = () => {
    features.useLoader();
    const { isSupport } = useIsSupport();

    const messageSublinks = useMessagesSublinks();
    const organizationSublinks = useOrganizationSublinks();
    const ipawsSublinks = useIpawsSublinks();
    const helpSublinks = useHelpSublinks();
    const settingsSublinks = useSettingsSublinks();

    return (
        <Stack component="nav" h="100%" dir="row" style={{ flexWrap: "wrap", gap: 0 }}>
            <Stack justify="space-between" h="100%" w={rem(50)} my={rem(3)}>
                <Stack justify="center" gap={0}>
                    {messageSublinks.length > 0 && <SidebarLink to="/messages" label="Messages" icon={IconInbox} />}
                    {organizationSublinks.length > 0 && <SidebarLink to="/organization" label="Organization" icon={IconServerCog} />}
                    {ipawsSublinks.length > 0 && <SidebarLink to="/ipaws" label="IPAWS" icon={IconIpaws} />}
                </Stack>
                <Stack justify="center" gap={0} mb="xs">
                    {isSupport && <SidebarLink to="/devtools" icon={IconLifebuoy} label="Support Tools" />}
                    {helpSublinks.length > 0 && <SidebarLink to="/help" label="Help" icon={IconHelp} />}
                    {settingsSublinks.length > 0 && <SidebarLink to="/settings" label="Settings" icon={IconSettings} />}
                </Stack>
            </Stack>
            <SecondaryNav />
        </Stack>
    );
};

export const NavbarFooter = () => {
    const messageSublinks = useMessagesSublinks();
    const organizationSublinks = useOrganizationSublinks();
    const ipawsSublinks = useIpawsSublinks();
    const helpSublinks = useHelpSublinks();
    const settingsSublinks = useSettingsSublinks();

    return (
        <Group h={rem(58)} justify="space-around">
            {messageSublinks.length > 0 && <SidebarLink to="/messages" label="Messages" icon={IconInbox} />}
            {organizationSublinks.length > 0 && <SidebarLink to="/organization" label="Organization" icon={IconServerCog} />}
            {ipawsSublinks.length > 0 && <SidebarLink to="/ipaws" label="IPAWS" icon={IconIpaws} />}
            {helpSublinks.length > 0 && <SidebarLink to="/help" label="Help" icon={IconHelp} />}
            {settingsSublinks.length > 0 && <SidebarLink to="/settings" label="Settings" icon={IconSettings} />}
        </Group>
    );
};
